import { apiClient, postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';
import { inputValueToUnix, unixToInputValue } from '@/shared/lib/datetime';
import { getListRows, getTotalPages } from '@/shared/lib/pagination';
import type { SelectOption } from '@/shared/ui/selectTypes';

const PROMOCODE_BASE = '/billing/billing-promo-code';
const CONDITION_BASE = '/billing/billing-promo-conditions';

const DEFAULT_CURRENCY = 'RUB';
const DEFAULT_COUNTRY_ID = 1;
const SECONDS_IN_DAY = 24 * 3600;
const GENERATE_TIMEOUT_MS = 5 * 60_000;

/** `type_id` of loyalty promocodes: they carry a validity period and a discount level instead of an amount. */
export const LOYALTY_PROMO_TYPE = '7';

// ---- promocodes -------------------------------------------------------------

export interface Promocode extends Record<string, unknown> {
  id: number;
  promo_id: number;
  code: string;
  amount: number | string | null;
  count: number | string | null;
  count_of_uses: number | string | null;
  type_id: number | string;
  currency?: { code?: string } | null;
  expired_time?: number | null;
  burn_time?: number | null;
  discount_level_id?: number | string | null;
  loyalty_days?: number | string | null;
}

export async function fetchPromocodes(promoId: number, page: number) {
  const res = await postForm(`${PROMOCODE_BASE}/index/`, { page, promo_id: promoId });
  return { items: getListRows<Promocode>(res, ['items']), totalPages: getTotalPages(res) };
}

export async function fetchPromoTypes(): Promise<SelectOption[]> {
  const { data } = await apiClient.get<{ promo_types?: Record<string, string> }>('/billing/billing-promo/get-types/');
  return Object.entries(data.promo_types ?? {}).map(([value, label]) => ({ value, label }));
}

export async function fetchDiscountLevels(): Promise<SelectOption[]> {
  const { data } = await apiClient.get<{ data?: Array<{ id: number | string; title: string }> }>(
    '/discount/level/list/',
  );
  return (data.data ?? []).map((level) => ({ value: String(level.id), label: level.title }));
}

export interface PromocodeValues {
  code: string;
  amount: string;
  count: string;
  typeId: string;
  /** `datetime-local` values. */
  expiredTime: string;
  burnTime: string;
  loyaltyDays: string;
  discountLevelId: string;
}

const nowInput = () => unixToInputValue(Date.now() / 1000);

export const emptyPromocode = (): PromocodeValues => ({
  code: '',
  amount: '',
  count: '',
  typeId: '',
  expiredTime: nowInput(),
  burnTime: nowInput(),
  loyaltyDays: '',
  discountLevelId: '',
});

const str = (value: unknown) => (value === null || value === undefined ? '' : String(value));

export const promocodeToValues = (item: Promocode): PromocodeValues => ({
  code: item.code ?? '',
  amount: str(item.amount),
  count: str(item.count),
  typeId: str(item.type_id),
  expiredTime: unixToInputValue(item.expired_time),
  burnTime: unixToInputValue(item.burn_time),
  loyaltyDays: str(item.loyalty_days),
  discountLevelId: str(item.discount_level_id),
});

export const savePromocode = (promoId: number, id: number | undefined, v: PromocodeValues) => {
  const isLoyalty = v.typeId === LOYALTY_PROMO_TYPE;
  return submitForm(`${PROMOCODE_BASE}/${id ? 'update' : 'create'}/`, {
    id,
    promo_id: promoId,
    code: v.code,
    count: v.count,
    currency: DEFAULT_CURRENCY,
    type_id: v.typeId,
    country_id: id ? undefined : DEFAULT_COUNTRY_ID,
    amount: isLoyalty ? undefined : v.amount,
    loyalty_days: isLoyalty ? v.loyaltyDays : undefined,
    discount_level_id: isLoyalty ? v.discountLevelId : undefined,
    expired_time: inputValueToUnix(v.expiredTime),
    burn_time: inputValueToUnix(v.burnTime),
  });
};

export interface GeneratePromocodesValues {
  amount: string;
  count: string;
  expiredTime: string;
}

export const emptyGeneratePromocodes = (): GeneratePromocodesValues => ({
  amount: '',
  count: '',
  expiredTime: nowInput(),
});

export const generatePromocodes = (promoId: number, v: GeneratePromocodesValues) =>
  submitForm(
    `${PROMOCODE_BASE}/generate/`,
    {
      promo_id: promoId,
      amount: v.amount,
      count: v.count,
      currency: DEFAULT_CURRENCY,
      expired_time: inputValueToUnix(v.expiredTime),
    },
    { timeout: GENERATE_TIMEOUT_MS },
  );

// ---- promo conditions -------------------------------------------------------

export interface ConditionAction {
  action: string;
  description: string;
}

export interface PromoCondition extends Record<string, unknown> {
  id: number;
  promotion_id: number;
  promotion_link?: string;
  type_bonus?: string;
  bonus?: number | string | null;
  burn?: number | boolean;
  count_use?: number | string | null;
  expire_time?: number | null;
  user_type?: { user_type?: number | string; description?: string } | null;
  utm?: Partial<Record<'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_term', string>> | null;
  actions?: ConditionAction[] | null;
}

export const fetchPromoConditions = async (promoId: number) =>
  getListRows<PromoCondition>(await postForm(`${CONDITION_BASE}/index/`, { promo_id: promoId }), [
    'promo_conditions',
  ]);

export const fetchConditionActions = async (promoId: number) =>
  getListRows<ConditionAction & Record<string, unknown>>(
    await postForm(`${CONDITION_BASE}/get-condition-actions/`, { promo_id: promoId }),
    ['available_actions'],
  );

/** Expiration shorter than a year is stored as a number of seconds (the form's "days"), longer — as a unix time. */
const MAX_RELATIVE_EXPIRE_SECONDS = 365 * SECONDS_IN_DAY;
export const isRelativeExpire = (expireTime: number | null | undefined) =>
  Number(expireTime) < MAX_RELATIVE_EXPIRE_SECONDS;

export const expireInDays = (expireTime: number | null | undefined) =>
  Math.round(Number(expireTime) / SECONDS_IN_DAY);

export const MAX_EXPIRE_DAYS = 364;

export interface PromoConditionValues {
  actions: string[];
  typeBonus: string;
  /** '0' / '1'. */
  burn: string;
  expireTime: string;
  days: string;
  bonus: string;
  countUse: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  userType: string;
}

export const emptyPromoCondition = (): PromoConditionValues => ({
  actions: [],
  typeBonus: '',
  burn: '',
  expireTime: nowInput(),
  days: '',
  bonus: '',
  countUse: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  utmTerm: '',
  userType: '',
});

export const conditionToValues = (item: PromoCondition): PromoConditionValues => {
  const relative = Boolean(item.expire_time) && isRelativeExpire(item.expire_time);
  return {
    actions: (item.actions ?? []).map(({ action }) => action),
    typeBonus: str(item.type_bonus),
    burn: item.burn ? '1' : '0',
    expireTime: relative ? nowInput() : unixToInputValue(item.expire_time) || nowInput(),
    days: relative ? String(expireInDays(item.expire_time)) : '',
    bonus: str(item.bonus),
    countUse: str(item.count_use),
    utmSource: item.utm?.utm_source ?? '',
    utmMedium: item.utm?.utm_medium ?? '',
    utmCampaign: item.utm?.utm_campaign ?? '',
    utmTerm: item.utm?.utm_term ?? '',
    userType: str(item.user_type?.user_type),
  };
};

export const savePromoCondition = (promoId: number, id: number | undefined, v: PromoConditionValues) =>
  submitForm(`${CONDITION_BASE}/${id ? 'edit' : 'create'}/`, {
    id,
    promotion_id: promoId,
    actions: v.actions,
    type_bonus: v.typeBonus,
    burn: v.burn,
    bonus: v.bonus,
    count_use: v.countUse,
    'utm[utm_source]': v.utmSource,
    'utm[utm_medium]': v.utmMedium,
    'utm[utm_campaign]': v.utmCampaign,
    'utm[utm_term]': v.utmTerm,
    user_type: v.userType,
    expire_time: v.days ? Number(v.days) * SECONDS_IN_DAY : inputValueToUnix(v.expireTime),
  });
